import { useState, useCallback, useEffect } from 'react';

const useTablePagination = ({
  initialPage = 0,
  initialRowsPerPage = 10,
  totalItems = 0,
  onPageChange,
  onRowsPerPageChange,
  isServerSide = false,
  externalPage,
  externalRowsPerPage,
}) => {
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Sync with external state if provided (only if different)
  useEffect(() => {
    if (externalPage !== undefined && externalPage !== page) {
      setPage(externalPage);
    }
  }, [externalPage, page]);

  useEffect(() => {
    if (externalRowsPerPage !== undefined && externalRowsPerPage !== rowsPerPage) {
      setRowsPerPage(externalRowsPerPage);
    }
  }, [externalRowsPerPage, rowsPerPage]);

  // Handle page change
  const handlePageChange = useCallback((event, newPage) => {
    if (isServerSide) {
      const nextPage = newPage;
      onPageChange?.(nextPage);
    } else {
      setPage(newPage);
    }
  }, [isServerSide, onPageChange]);

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback((event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    onRowsPerPageChange?.(newRowsPerPage);
  }, [onRowsPerPageChange]);

  // Calculate pagination info
  const getPaginationInfo = useCallback(() => {
    const currentPage = isServerSide ? page + 1 : page;
    const startItem = (currentPage - 1) * rowsPerPage + 1;
    const endItem = Math.min(currentPage * rowsPerPage, totalItems);

    return {
      currentPage,
      rowsPerPage,
      startItem,
      endItem,
      totalItems,
    };
  }, [page, rowsPerPage, totalItems, isServerSide]);

  return {
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    getPaginationInfo,
  };
};

export default useTablePagination;
